import { createContext, useContext, useEffect } from 'react';

// What a drawn visual shows when it cannot be drawn.
//
// Visuals are checked before they are saved (backend lesson/visual_check.py),
// so this is the rare one that still fails in the browser. In the lesson text
// it leaves a short note where the figure was; inside a card, a quiz question
// or a flashcard (`BlockMarkdown`) it shows nothing - the card keeps its text
// and its size. Never the raw spec: a block of JSON in a lesson reads as a
// broken page. Each kind is reported once per session.

export type FenceFallback = 'note' | 'hide';

export const FenceFallbackContext = createContext<FenceFallback>('note');

const NOUN: Record<string, string> = {
  mermaid: 'diagram',
  chart: 'chart',
  funcplot: 'graph',
  smiles: 'molecule',
  music: 'score',
  geometry: 'figure',
  venn: 'diagram',
  numberline: 'number line',
};

const reported = new Set<string>();

export function FenceFailed({ kind }: { kind: string }) {
  const mode = useContext(FenceFallbackContext);
  useEffect(() => {
    if (reported.has(kind)) return;
    reported.add(kind);
    // Loaded when needed: the markdown module also runs in Node (the eval's
    // render check), where the browser observability setup cannot load.
    void import('@/config/observability')
      .then(({ captureException }) =>
        captureException(new Error(`A ${kind} visual failed to render`), {
          fence: kind,
        })
      )
      .catch(() => undefined);
  }, [kind]);
  if (mode === 'hide') return null;
  return (
    <p
      role='note'
      data-fence-failed={kind}
      className='my-3 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground'
    >
      This {NOUN[kind] ?? 'visual'} couldn&apos;t be drawn.
    </p>
  );
}
